import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';

const VoiceContext = createContext(null);
export const useVoice = () => useContext(VoiceContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const RTC_CONFIG = {
  iceServers: DEFAULT_ICE_SERVERS,
  iceTransportPolicy: 'all',
  bundlePolicy: 'max-bundle',
  rtcpMuxPolicy: 'require',
};

const PTT_KEY = 'v';

export const VoiceProvider = ({ children }) => {
  const { user, token } = useAuth();
  const { on, send, connected } = useSocket();
  const [voiceChannel, setVoiceChannel] = useState(null);
  const [voiceUsers, setVoiceUsers] = useState([]);
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);
  const [pushToTalk, setPushToTalk] = useState(false);
  const [speaking, setSpeaking] = useState({});
  const [connecting, setConnecting] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [localVideoStream, setLocalVideoStream] = useState(null);

  const localStreamRef = useRef(null);
  const localVideoStreamRef = useRef(null);
  const peersRef = useRef({});
  const analyserRef = useRef({});
  const makingOfferRef = useRef({});
  const politeRef = useRef({});
  const voiceChannelRef = useRef(null);
  const disconnectTimeoutRef = useRef({});
  const iceServersRef = useRef(null);
  const videoSendersRef = useRef({});
  const transceiversRef = useRef({});
  const remoteMediaRef = useRef({});
  const pendingCandidatesRef = useRef({});
  const isSettingRemoteDescRef = useRef({});

  const fetchVoiceStates = useCallback(async () => {
    if (!token) return [];
    try {
      const res = await fetch(`${API_URL}/api/voice/states`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const states = await res.json();
        setVoiceUsers(states);
        return states;
      }
    } catch (e) { console.error('Failed to fetch voice states:', e); }
    return [];
  }, [token]);

  const fetchIceServers = useCallback(async () => {
    if (iceServersRef.current) return iceServersRef.current;
    if (!token) {
      iceServersRef.current = DEFAULT_ICE_SERVERS;
      return DEFAULT_ICE_SERVERS;
    }
    try {
      const res = await fetch(`${API_URL}/api/ice-servers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.iceServers && data.iceServers.length > 0) {
          iceServersRef.current = data.iceServers;
          return data.iceServers;
        }
      }
    } catch (e) {
      console.error('Failed to fetch ICE servers:', e);
    }
    iceServersRef.current = DEFAULT_ICE_SERVERS;
    return DEFAULT_ICE_SERVERS;
  }, [token]);

  useEffect(() => { fetchVoiceStates(); }, [fetchVoiceStates]);

  useEffect(() => {
    localVideoStreamRef.current = localVideoStream;
  }, [localVideoStream]);

  const cleanupPeer = useCallback((userId) => {
    console.log('[Voice] Cleaning up peer:', userId);
    if (peersRef.current[userId]) {
      peersRef.current[userId].close();
      delete peersRef.current[userId];
    }
    if (disconnectTimeoutRef.current[userId]) {
      clearTimeout(disconnectTimeoutRef.current[userId]);
      delete disconnectTimeoutRef.current[userId];
    }
    delete videoSendersRef.current[userId];
    delete transceiversRef.current[userId];
    delete remoteMediaRef.current[userId];
    delete pendingCandidatesRef.current[userId];
    delete makingOfferRef.current[userId];
    delete politeRef.current[userId];
    delete isSettingRemoteDescRef.current[userId];
    
    const audioEl = document.getElementById(`audio-${userId}`);
    if (audioEl) audioEl.remove();
    if (analyserRef.current[userId]) {
      analyserRef.current[userId].ctx.close().catch(() => {});
      delete analyserRef.current[userId];
    }
    setRemoteStreams(prev => {
      if (!prev[userId]) return prev;
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  }, [setRemoteStreams]);

  const cleanupAll = useCallback(() => {
    Object.keys(peersRef.current).forEach(cleanupPeer);
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
  }, [cleanupPeer]);

  // V2 FIX: Perfect Negotiation with explicit state tracking
  const negotiatePeer = useCallback(async (remoteUserId, pc) => {
    if (!pc || pc.connectionState === 'closed') {
      console.log('[Voice] Skip negotiation - no pc or closed');
      return;
    }
    
    if (makingOfferRef.current[remoteUserId]) {
      console.log('[Voice] Skip negotiation - already making offer for', remoteUserId);
      return;
    }

    if (isSettingRemoteDescRef.current[remoteUserId]) {
      console.log('[Voice] Skip negotiation - setting remote desc for', remoteUserId);
      return;
    }
    
    try {
      makingOfferRef.current[remoteUserId] = true;
      console.log('[Voice] Creating offer for', remoteUserId, 'state:', pc.signalingState);
      
      const offer = await pc.createOffer();
      
      // Check if state changed during createOffer
      if (pc.signalingState !== 'stable') {
        console.log('[Voice] State changed during createOffer, aborting');
        return;
      }
      
      await pc.setLocalDescription(offer);
      
      console.log('[Voice] Sending offer to', remoteUserId);
      if (send && pc.localDescription) {
        send({
          type: 'VOICE_OFFER',
          target_user_id: remoteUserId,
          sdp: pc.localDescription,
          channel_id: voiceChannelRef.current,
        });
      }
    } catch (err) {
      console.error('[Voice] Negotiation error for', remoteUserId, ':', err.message);
    } finally {
      makingOfferRef.current[remoteUserId] = false;
    }
  }, [send]);

  const createPeer = useCallback((remoteUserId, shouldInitiate = false) => {
    // Close existing peer if any
    if (peersRef.current[remoteUserId]) {
      console.log('[Voice] Closing existing peer for', remoteUserId);
      peersRef.current[remoteUserId].close();
      delete peersRef.current[remoteUserId];
    }

    console.log('[Voice] Creating peer for', remoteUserId, 'shouldInitiate:', shouldInitiate);
    
    const pc = new RTCPeerConnection({
      ...RTC_CONFIG,
      iceServers: iceServersRef.current || DEFAULT_ICE_SERVERS,
    });
    peersRef.current[remoteUserId] = pc;
    
    // V2 FIX: Consistent polite determination
    // The peer with the LOWER user_id is "polite" (will yield in collisions)
    // String comparison works for UUIDs
    const myId = user?.user_id || '';
    politeRef.current[remoteUserId] = myId < remoteUserId;
    console.log('[Voice] Polite status:', politeRef.current[remoteUserId], 
      '(my:', myId.slice(-6), 'remote:', remoteUserId.slice(-6), ')');

    pendingCandidatesRef.current[remoteUserId] = [];
    makingOfferRef.current[remoteUserId] = false;
    isSettingRemoteDescRef.current[remoteUserId] = false;

    // V2 FIX: Only trigger negotiation if we should initiate
    pc.onnegotiationneeded = async () => {
      console.log('[Voice] onnegotiationneeded for', remoteUserId);
      // Only initiate if we explicitly should (prevents both sides initiating)
      if (shouldInitiate) {
        await negotiatePeer(remoteUserId, pc);
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && send) {
        send({
          type: 'VOICE_ICE_CANDIDATE',
          target_user_id: remoteUserId,
          candidate: event.candidate,
          channel_id: voiceChannelRef.current,
        });
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[Voice] ICE state for', remoteUserId, ':', pc.iceConnectionState);
      if (pc.iceConnectionState === 'failed') {
        console.log('[Voice] ICE failed, restarting');
        pc.restartIce();
      }
    };

    // Add local audio tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log('[Voice] Adding local track:', track.kind);
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // Add video transceivers
    const cameraTransceiver = pc.addTransceiver('video', { direction: 'sendrecv' });
    const screenTransceiver = pc.addTransceiver('video', { direction: 'sendrecv' });
    transceiversRef.current[remoteUserId] = { camera: cameraTransceiver, screen: screenTransceiver };
    videoSendersRef.current[remoteUserId] = { camera: cameraTransceiver.sender, screen: screenTransceiver.sender };
    
    const localVideoTrack = localVideoStreamRef.current?.getVideoTracks?.()[0];
    if (localVideoTrack) {
      cameraTransceiver.sender.replaceTrack(localVideoTrack).catch(() => {});
    }

    // Handle remote tracks
    pc.ontrack = (event) => {
      const track = event.track;
      console.log('[Voice] Received track from', remoteUserId, ':', track.kind);
      
      let remoteStream = remoteMediaRef.current[remoteUserId];
      if (!remoteStream) {
        remoteStream = new MediaStream();
        remoteMediaRef.current[remoteUserId] = remoteStream;
      }
      if (!remoteStream.getTracks().some(t => t.id === track.id)) {
        remoteStream.addTrack(track);
      }

      if (track.kind === 'audio') {
        const audioId = `audio-${remoteUserId}`;
        let audioEl = document.getElementById(audioId);
        if (!audioEl) {
          audioEl = document.createElement('audio');
          audioEl.id = audioId;
          audioEl.autoplay = true;
          audioEl.playsInline = true;
          audioEl.volume = 1.0;
          document.body.appendChild(audioEl);
        }
        const audioStream = new MediaStream();
        remoteStream.getAudioTracks().forEach(t => audioStream.addTrack(t));
        audioEl.srcObject = audioStream;
        audioEl.muted = false;
        
        const playAudio = () => {
          audioEl.play().then(() => {
            console.log('[Voice] Audio playing for', remoteUserId);
          }).catch(err => {
            console.warn('[Voice] Audio play retry needed');
            setTimeout(playAudio, 500);
          });
        };
        playAudio();

        try {
          if (!analyserRef.current[remoteUserId]) {
            const ctx = new AudioContext();
            const source = ctx.createMediaStreamSource(remoteStream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            analyserRef.current[remoteUserId] = { analyser, ctx };
          }
        } catch { }
      }

      if (track.kind === 'video') {
        const role = transceiversRef.current[remoteUserId]?.screen === event.transceiver ? 'screen' : 'camera';
        track.onended = () => {
          setRemoteStreams(prev => {
            const existing = prev[remoteUserId];
            if (!existing) return prev;
            const nextUser = { ...existing };
            delete nextUser[role];
            if (Object.keys(nextUser).length === 0) {
              const next = { ...prev };
              delete next[remoteUserId];
              return next;
            }
            return { ...prev, [remoteUserId]: nextUser };
          });
        };
        setRemoteStreams(prev => ({
          ...prev,
          [remoteUserId]: { ...(prev[remoteUserId] || {}), [role]: remoteStream }
        }));
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('[Voice] Connection state for', remoteUserId, ':', pc.connectionState);
      if (pc.connectionState === 'connected') {
        console.log('[Voice] ✓ Connected to', remoteUserId);
        if (disconnectTimeoutRef.current[remoteUserId]) {
          clearTimeout(disconnectTimeoutRef.current[remoteUserId]);
          delete disconnectTimeoutRef.current[remoteUserId];
        }
      }
      if (pc.connectionState === 'failed') {
        cleanupPeer(remoteUserId);
      }
      if (pc.connectionState === 'disconnected') {
        disconnectTimeoutRef.current[remoteUserId] = setTimeout(() => {
          if (peersRef.current[remoteUserId]?.connectionState === 'disconnected') {
            cleanupPeer(remoteUserId);
          }
        }, 5000);
      }
    };

    return pc;
  }, [send, cleanupPeer, user, negotiatePeer]);

  // V2 FIX: Process pending ICE candidates
  const processPendingCandidates = useCallback(async (remoteUserId, pc) => {
    const pending = pendingCandidatesRef.current[remoteUserId] || [];
    if (pending.length === 0) return;
    
    console.log('[Voice] Processing', pending.length, 'pending ICE candidates for', remoteUserId);
    pendingCandidatesRef.current[remoteUserId] = [];
    
    for (const candidate of pending) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.debug('[Voice] Pending ICE error (ok):', e.message);
      }
    }
  }, []);

  useEffect(() => {
    if (!connected) return;
    const channelId = voiceChannelRef.current;
    if (!channelId) return;
    
    send({ type: 'VOICE_JOIN', channel_id: channelId });
    send({
      type: 'VOICE_STATE',
      muted,
      deafened,
      camera: cameraOn,
      screen: screenSharing,
    });
  }, [connected, send, muted, deafened, cameraOn, screenSharing]);

  useEffect(() => {
    if (!connected || !voiceChannel) return;
    send({ type: 'VOICE_JOIN', channel_id: voiceChannel });
  }, [connected, voiceChannel, send]);

  const ensureAudioForPeers = useCallback(() => {
    const audioTrack = localStreamRef.current?.getAudioTracks?.()[0];
    if (!audioTrack || !localStreamRef.current) return;
    Object.entries(peersRef.current).forEach(([remoteUserId, pc]) => {
      const hasAudioSender = pc.getSenders().some(s => s.track?.kind === 'audio');
      if (!hasAudioSender) {
        pc.addTrack(audioTrack, localStreamRef.current);
      }
    });
  }, []);

  // Join voice channel
  const joinVoice = useCallback(async (channelId) => {
    if (!send || !user) return;
    setConnecting(true);
    
    try {
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        localStreamRef.current = stream;
      } catch {
        console.warn('No mic available');
      }

      await fetchIceServers();
      setVoiceChannel(channelId);
      voiceChannelRef.current = channelId;
      setRemoteStreams({});
      setDeafened(false);
      const shouldMute = !stream || pushToTalk;
      setMuted(shouldMute);
      
      if (stream) {
        stream.getAudioTracks().forEach(t => { t.enabled = !shouldMute; });
      }
      
      if (user) {
        setVoiceUsers(prev => {
          const filtered = prev.filter(v => v.user_id !== user.user_id);
          return [...filtered, {
            user_id: user.user_id,
            user,
            channel_id: channelId,
            muted: shouldMute,
            deafened: false,
            camera: false,
            screen: false,
          }];
        });
      }
      
      if (send) send({ type: 'VOICE_STATE', muted: shouldMute });
      send({ type: 'VOICE_JOIN', channel_id: channelId });

      if (stream) {
        try {
          const ctx = new AudioContext();
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);
          analyserRef.current['_local'] = { analyser, ctx };
        } catch { }
      }

      // V2 FIX: Fetch existing peers and create connections
      // Only the NEW user initiates to EXISTING users
      const states = await fetchVoiceStates();
      const existingPeers = states.filter(v => 
        v.channel_id === channelId && v.user_id !== user.user_id
      );
      
      console.log('[Voice] Found', existingPeers.length, 'existing peers in channel');
      
      for (const p of existingPeers) {
        // The joining user initiates connections to existing users
        createPeer(p.user_id, true);
      }
      
    } catch (e) {
      console.error('Join voice error:', e);
    } finally {
      setConnecting(false);
    }
  }, [send, user, pushToTalk, fetchIceServers, fetchVoiceStates, createPeer]);

  const leaveVoice = useCallback(() => {
    if (send) send({ type: 'VOICE_LEAVE' });
    if (localVideoStream) {
      localVideoStream.getTracks().forEach(t => t.stop());
      setLocalVideoStream(null);
    }
    setCameraOn(false);
    setScreenSharing(false);
    setRemoteStreams({});
    cleanupAll();
    setVoiceChannel(null);
    voiceChannelRef.current = null;
    setVoiceUsers(prev => prev.filter(v => v.user_id !== user?.user_id));
  }, [send, cleanupAll, user, localVideoStream]);

  const setMuteState = useCallback((newMuted) => {
    setMuted(newMuted);
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = !newMuted; });
    }
    if (send) send({ type: 'VOICE_STATE', muted: newMuted });
  }, [send]);

  const toggleMute = useCallback(() => setMuteState(!muted), [muted, setMuteState]);

  const toggleDeafen = useCallback(() => {
    const newDeafened = !deafened;
    setDeafened(newDeafened);
    document.querySelectorAll('audio[id^="audio-"]').forEach(el => { el.muted = newDeafened; });
    if (send) send({ type: 'VOICE_STATE', deafened: newDeafened });
    if (newDeafened && !muted) setMuteState(true);
  }, [deafened, muted, send, setMuteState]);

  const togglePushToTalk = useCallback(() => {
    setPushToTalk(prev => {
      const next = !prev;
      if (next) setMuteState(true);
      return next;
    });
  }, [setMuteState]);

  const setVideoTrackForPeers = useCallback((role, track) => {
    Object.entries(videoSendersRef.current).forEach(([_, senders]) => {
      const sender = senders?.[role];
      if (sender) sender.replaceTrack(track).catch(() => {});
    });
  }, []);

  const toggleCamera = useCallback(async () => {
    if (cameraOn) {
      if (localVideoStream) {
        localVideoStream.getTracks().forEach(t => t.stop());
        setLocalVideoStream(null);
      }
      setVideoTrackForPeers('camera', null);
      setCameraOn(false);
      setVoiceUsers(prev => prev.map(v => v.user_id === user?.user_id ? { ...v, camera: false } : v));
      if (send) send({ type: 'VOICE_STATE', camera: false });
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        setLocalVideoStream(stream);
        setCameraOn(true);
        setVoiceUsers(prev => prev.map(v => v.user_id === user?.user_id ? { ...v, camera: true } : v));
        setVideoTrackForPeers('camera', stream.getVideoTracks()[0] || null);
      } catch (e) {
        console.error('Camera denied:', e);
      }
    }
  }, [cameraOn, localVideoStream, send, setVideoTrackForPeers, user]);

  const toggleScreenShare = useCallback(async () => {
    if (screenSharing) {
      if (localVideoStream) {
        localVideoStream.getTracks().forEach(t => t.stop());
        setLocalVideoStream(null);
      }
      setScreenSharing(false);
      setVideoTrackForPeers('screen', null);
      setVoiceUsers(prev => prev.map(v => v.user_id === user?.user_id ? { ...v, screen: false } : v));
      if (send) send({ type: 'VOICE_STATE', screen: false });
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
        const track = stream.getVideoTracks()[0];
        if (track) {
          track.onended = () => {
            setScreenSharing(false);
            setVideoTrackForPeers('screen', null);
            setLocalVideoStream(null);
            setVoiceUsers(prev => prev.map(v => v.user_id === user?.user_id ? { ...v, screen: false } : v));
            if (send) send({ type: 'VOICE_STATE', screen: false });
          };
        }
        if (cameraOn && localVideoStream) {
          localVideoStream.getTracks().forEach(t => t.stop());
          setCameraOn(false);
          setVoiceUsers(prev => prev.map(v => v.user_id === user?.user_id ? { ...v, camera: false } : v));
          if (send) send({ type: 'VOICE_STATE', camera: false });
        }
        setLocalVideoStream(stream);
        setScreenSharing(true);
        setVoiceUsers(prev => prev.map(v => v.user_id === user?.user_id ? { ...v, screen: true } : v));
        setVideoTrackForPeers('screen', track || null);
      } catch (e) {
        console.error('Screen share denied:', e);
      }
    }
  }, [screenSharing, cameraOn, localVideoStream, send, setVideoTrackForPeers, user]);

  // Push-to-talk
  useEffect(() => {
    if (!pushToTalk || !voiceChannel) return;
    const isEditable = (target) => {
      if (!target) return false;
      return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
    };

    const handleKeyDown = (e) => {
      if (e.key?.toLowerCase() !== PTT_KEY || isEditable(e.target) || e.repeat) return;
      if (muted) setMuteState(false);
    };

    const handleKeyUp = (e) => {
      if (e.key?.toLowerCase() !== PTT_KEY || isEditable(e.target)) return;
      if (!muted) setMuteState(true);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pushToTalk, voiceChannel, muted, setMuteState]);

  // Speaking detection
  useEffect(() => {
    const interval = setInterval(() => {
      const newSpeaking = {};
      Object.entries(analyserRef.current).forEach(([id, { analyser }]) => {
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        const userId = id === '_local' ? user?.user_id : id;
        if (userId) newSpeaking[userId] = avg > 15;
      });
      setSpeaking(prev => {
        const changed = Object.keys(newSpeaking).some(k => prev[k] !== newSpeaking[k]);
        return changed ? { ...prev, ...newSpeaking } : prev;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [user]);

  // Periodic state refresh
  useEffect(() => {
    if (!voiceChannel) return;
    const interval = setInterval(fetchVoiceStates, 2000);
    return () => clearInterval(interval);
  }, [voiceChannel, fetchVoiceStates]);

  // V2 FIX: WebSocket handlers with proper Perfect Negotiation
  useEffect(() => {
    if (!on) return;
    
    const unsubs = [
      on('VOICE_STATE_UPDATE', (e) => {
        const d = e.data;
        
        if (d.action === 'join') {
          setVoiceUsers(prev => {
            const filtered = prev.filter(v => v.user_id !== d.user_id);
            return [...filtered, {
              user_id: d.user_id,
              user: d.user,
              channel_id: d.channel_id,
              muted: d.voice_state?.muted || false,
              deafened: d.voice_state?.deafened || false,
              camera: d.voice_state?.camera || false,
              screen: d.voice_state?.screen || false,
            }];
          });

          const currentChannel = voiceChannelRef.current;
          
          // V2 FIX: Only existing users create peer (don't initiate)
          // The joining user will initiate connections
          if (currentChannel && currentChannel === d.channel_id && d.user_id !== user?.user_id) {
            console.log('[Voice] New user joined:', d.user_id, '- creating peer (waiting for their offer)');
            fetchIceServers().then(() => {
              // Don't initiate - wait for the joining user to send offer
              createPeer(d.user_id, false);
            });
          }

          if (d.user_id === user?.user_id) {
            fetchVoiceStates();
          }
          
        } else if (d.action === 'leave') {
          setVoiceUsers(prev => prev.filter(v => v.user_id !== d.user_id));
          cleanupPeer(d.user_id);
          
        } else if (d.action === 'state_change') {
          setVoiceUsers(prev => prev.map(v =>
            v.user_id === d.user_id ? { ...v, ...d.voice_state } : v
          ));
          if (d.user_id && d.voice_state) {
            setRemoteStreams(prev => {
              const existing = prev[d.user_id];
              if (!existing) return prev;
              const nextUser = { ...existing };
              if (!d.voice_state.camera) delete nextUser.camera;
              if (!d.voice_state.screen) delete nextUser.screen;
              if (Object.keys(nextUser).length === 0) {
                const next = { ...prev };
                delete next[d.user_id];
                return next;
              }
              return { ...prev, [d.user_id]: nextUser };
            });
          }
        }
      }),

      // V2 FIX: Improved offer handling
      on('VOICE_OFFER', async (e) => {
        const { from_user_id, sdp } = e.data;
        console.log('[Voice] Received OFFER from:', from_user_id);
        
        if (!voiceChannelRef.current) {
          console.log('[Voice] Ignoring offer - not in channel');
          return;
        }
        
        await fetchIceServers();
        
        let pc = peersRef.current[from_user_id];
        if (!pc) {
          console.log('[Voice] Creating peer for incoming offer from:', from_user_id);
          pc = createPeer(from_user_id, false);
        }
        
        const polite = politeRef.current[from_user_id];
        const makingOffer = makingOfferRef.current[from_user_id];
        const notStable = pc.signalingState !== 'stable';
        const offerCollision = makingOffer || notStable;
        
        console.log('[Voice] Offer collision check:', { polite, makingOffer, state: pc.signalingState, collision: offerCollision });
        
        // Impolite peer ignores collision offers
        if (!polite && offerCollision) {
          console.log('[Voice] IGNORING offer (impolite + collision)');
          return;
        }
        
        try {
          isSettingRemoteDescRef.current[from_user_id] = true;
          
          // Polite peer: if collision, rollback first
          if (polite && offerCollision) {
            console.log('[Voice] Polite peer rolling back for collision');
            await pc.setLocalDescription({ type: 'rollback' });
          }
          
          console.log('[Voice] Setting remote description (offer)');
          await pc.setRemoteDescription(new RTCSessionDescription(sdp));
          
          // Process pending ICE candidates
          await processPendingCandidates(from_user_id, pc);
          
          // Create and send answer
          console.log('[Voice] Creating answer');
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          
          console.log('[Voice] Sending ANSWER to:', from_user_id);
          if (send) {
            send({
              type: 'VOICE_ANSWER',
              target_user_id: from_user_id,
              sdp: pc.localDescription,
              channel_id: voiceChannelRef.current,
            });
          }
        } catch (err) {
          console.error('[Voice] Offer handling error:', err.message);
        } finally {
          isSettingRemoteDescRef.current[from_user_id] = false;
        }
      }),

      // V2 FIX: Answer handling
      on('VOICE_ANSWER', async (e) => {
        const { from_user_id, sdp } = e.data;
        console.log('[Voice] Received ANSWER from:', from_user_id);
        
        const pc = peersRef.current[from_user_id];
        if (!pc) {
          console.log('[Voice] No peer for answer');
          return;
        }
        
        if (pc.signalingState !== 'have-local-offer') {
          console.log('[Voice] Ignoring answer - wrong state:', pc.signalingState);
          return;
        }
        
        try {
          isSettingRemoteDescRef.current[from_user_id] = true;
          await pc.setRemoteDescription(new RTCSessionDescription(sdp));
          console.log('[Voice] Answer applied successfully');
          
          // Process pending ICE candidates
          await processPendingCandidates(from_user_id, pc);
        } catch (err) {
          console.error('[Voice] Answer error:', err.message);
        } finally {
          isSettingRemoteDescRef.current[from_user_id] = false;
        }
      }),

      // V2 FIX: ICE candidate queueing
      on('VOICE_ICE_CANDIDATE', async (e) => {
        const { from_user_id, candidate } = e.data;
        if (!candidate) return;
        
        const pc = peersRef.current[from_user_id];
        
        // Queue if no peer or no remote description yet
        if (!pc || !pc.remoteDescription?.type) {
          if (!pendingCandidatesRef.current[from_user_id]) {
            pendingCandidatesRef.current[from_user_id] = [];
          }
          pendingCandidatesRef.current[from_user_id].push(candidate);
          return;
        }
        
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.debug('[Voice] ICE error (ok):', err.message);
        }
      }),
    ];
    
    return () => unsubs.forEach(u => u?.());
  }, [on, send, user, createPeer, cleanupPeer, fetchVoiceStates, fetchIceServers, processPendingCandidates]);

  return (
    <VoiceContext.Provider value={{
      voiceChannel, voiceUsers, muted, deafened, speaking, connecting,
      cameraOn, screenSharing, localVideoStream, remoteStreams,
      pushToTalk, joinVoice, leaveVoice, toggleMute, toggleDeafen, toggleCamera, toggleScreenShare, togglePushToTalk,
    }}>
      {children}
    </VoiceContext.Provider>
  );
};
